import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Flame,
  X,
  Printer,
  RefreshCw,
  Users,
  ShieldAlert,
  CheckSquare,
  Square,
  UserCheck,
  Briefcase,
  MapPin,
  AlertTriangle,
} from 'lucide-react';

export default function FireEvacuationModal({ isOpen, onClose }) {
  const [rollCall, setRollCall] = useState({
    total_on_site: 0,
    office_headcount: 0,
    off_site_staff_count: 0,
    visitor_count: 0,
    staff_count: 0,
    staff_office_count: 0,
    staff_off_site_count: 0,
    visitors: [],
    staff: [],
  });
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [pinError, setPinError] = useState(false);
  const [activeTab, setActiveTab] = useState('office'); // 'office' | 'visitors' | 'staff' | 'offsite' | 'all'
  const [checkedIds, setCheckedIds] = useState(new Set());

  const fetchRollCall = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/visitors/combined-roll-call');
      setRollCall(
        res.data || {
          total_on_site: 0,
          office_headcount: 0,
          off_site_staff_count: 0,
          visitor_count: 0,
          staff_count: 0,
          staff_office_count: 0,
          staff_off_site_count: 0,
          visitors: [],
          staff: [],
        }
      );
    } catch (e) {
      console.error('Error fetching combined emergency roll call:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchRollCall();
    } else {
      setPin('');
      setUnlocked(false);
      setPinError(false);
      setCheckedIds(new Set());
    }
  }, [isOpen]);

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === '1234' || pin === '0000') {
      setUnlocked(true);
      setPinError(false);
    } else {
      setPinError(true);
      setPin('');
    }
  };

  const handleToggleCheck = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (!isOpen) return null;

  const allPersonnel = [
    ...rollCall.visitors.map((v) => ({ ...v, recordType: 'Visitor', rollId: v.id || v.session_id, is_off_site: false })),
    ...rollCall.staff.map((s) => ({ ...s, recordType: 'Staff', rollId: s.id || s.user_id })),
  ];

  const inOfficePersonnel = allPersonnel.filter((p) => !p.is_off_site);
  const offSitePersonnel = allPersonnel.filter((p) => p.is_off_site);

  const displayedList =
    activeTab === 'office'
      ? inOfficePersonnel
      : activeTab === 'visitors'
      ? allPersonnel.filter((p) => p.recordType === 'Visitor')
      : activeTab === 'staff'
      ? allPersonnel.filter((p) => p.recordType === 'Staff')
      : activeTab === 'offsite'
      ? offSitePersonnel
      : allPersonnel;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-in fade-in duration-200">
      <div className="w-full max-w-4xl rounded-3xl bg-slate-900 border border-red-500/40 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-red-950/90 to-slate-900 border-b border-red-500/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-red-600/30 border border-red-500 flex items-center justify-center text-red-400 shadow-md">
              <Flame className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <h2 className="text-xl font-black text-white flex items-center gap-2 font-display">
                Fire Safety & Emergency Evacuation
              </h2>
              <p className="text-xs text-red-300 font-medium">Head Office Building Manifest vs Off-Site Field Engineers</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* PIN Entry if locked */}
        {!unlocked ? (
          <div className="p-10 flex flex-col items-center justify-center text-center space-y-5">
            <div className="w-16 h-16 rounded-3xl bg-slate-800 flex items-center justify-center text-slate-300 border border-white/10 shadow-lg">
              <ShieldAlert className="w-8 h-8 text-amber-400" />
            </div>
            <div>
              <h3 className="text-xl font-extrabold text-white font-display">Fire Warden & Staff Access</h3>
              <p className="text-xs text-slate-400 mt-1 max-w-xs">
                Enter Reception PIN to unlock the live building evacuation register
              </p>
            </div>

            <form onSubmit={handlePinSubmit} className="w-full max-w-xs space-y-4">
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="PIN (Default: 1234)"
                className="w-full text-center tracking-widest text-3xl font-mono py-3 rounded-2xl bg-slate-800 border border-white/20 text-white focus:outline-none focus:border-red-500 shadow-inner"
                autoFocus
              />
              {pinError && <p className="text-xs text-red-400 font-semibold">Incorrect PIN. Try 1234</p>}
              <button
                type="submit"
                className="w-full py-3.5 rounded-2xl bg-red-600 hover:bg-red-500 text-white font-black text-sm shadow-xl transition-all active:scale-98"
              >
                Unlock Emergency Register
              </button>
            </form>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Action Bar & Tabs */}
            <div className="px-6 py-3.5 bg-slate-800/80 border-b border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm">
              {/* Headcount Pills */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-slate-200 font-bold mr-2">
                  <Users className="w-4 h-4 text-emerald-400" />
                  <span>In Office:</span>
                  <span className="px-2.5 py-0.5 rounded-full bg-red-500/20 text-red-300 font-extrabold border border-red-500/40 text-xs font-mono">
                    {rollCall.office_headcount ?? inOfficePersonnel.length}
                  </span>
                </div>

                {/* Filter Tabs */}
                <div className="flex items-center bg-slate-900 rounded-xl p-1 border border-white/10 text-xs flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => setActiveTab('office')}
                    className={`px-3 py-1 rounded-lg font-bold transition-all ${
                      activeTab === 'office' ? 'bg-cyan-500 text-slate-950 shadow' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Head Office ({inOfficePersonnel.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('visitors')}
                    className={`px-3 py-1 rounded-lg font-bold transition-all ${
                      activeTab === 'visitors' ? 'bg-cyan-500 text-slate-950 shadow' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Visitors ({rollCall.visitor_count})
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('staff')}
                    className={`px-3 py-1 rounded-lg font-bold transition-all ${
                      activeTab === 'staff' ? 'bg-cyan-500 text-slate-950 shadow' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Staff ({rollCall.staff_count})
                  </button>
                  {offSitePersonnel.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('offsite')}
                      className={`px-3 py-1 rounded-lg font-bold transition-all ${
                        activeTab === 'offsite'
                          ? 'bg-amber-500 text-slate-950 shadow'
                          : 'text-amber-400 hover:text-amber-300'
                      }`}
                    >
                      Off-Site Jobs ({offSitePersonnel.length})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setActiveTab('all')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition-all ${
                      activeTab === 'all' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-300'
                    }`}
                  >
                    All ({allPersonnel.length})
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={fetchRollCall}
                  disabled={loading}
                  className="px-3.5 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-750 text-slate-300 text-xs font-semibold flex items-center gap-1.5 border border-white/10 transition-colors"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-cyan-400' : ''}`} />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="px-3.5 py-1.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-slate-950 text-xs font-black flex items-center gap-1.5 shadow transition-colors"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Print Headcount
                </button>
              </div>
            </div>

            {/* Off-Site Notice Banner if any personnel are working on client sites */}
            {offSitePersonnel.length > 0 && (
              <div className="px-6 py-2 bg-amber-950/40 border-b border-amber-500/30 flex items-center gap-2 text-xs text-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <span>
                  <strong>Safety Notice:</strong> {offSitePersonnel.length} personnel are signed in for off-site customer jobs and are not inside Head Office.
                </span>
              </div>
            )}

            {/* Checklist items list */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-2.5">
              {displayedList.length === 0 ? (
                <div className="text-center py-16 text-slate-400 text-sm">
                  No personnel currently registered in this view.
                </div>
              ) : (
                displayedList.map((p) => {
                  const isChecked = checkedIds.has(p.rollId);
                  const isStaff = p.recordType === 'Staff';

                  return (
                    <div
                      key={p.rollId}
                      className={`p-4 rounded-2xl border transition-all flex items-center justify-between gap-4 ${
                        isChecked
                          ? 'bg-emerald-950/30 border-emerald-500/40 opacity-75'
                          : 'bg-slate-850/80 border-white/10 hover:border-slate-600'
                      }`}
                    >
                      {/* Checkbox button */}
                      <button
                        type="button"
                        onClick={() => handleToggleCheck(p.rollId)}
                        className={`p-2 rounded-xl border transition-colors flex items-center gap-2 text-xs font-bold ${
                          isChecked
                            ? 'bg-emerald-500 text-slate-950 border-emerald-400'
                            : 'bg-slate-800 text-slate-400 border-white/10 hover:text-white'
                        }`}
                        title="Mark verified safe at assembly point"
                      >
                        {isChecked ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                        <span className="hidden sm:inline">{isChecked ? 'Safe' : 'Check'}</span>
                      </button>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`font-bold text-base ${isChecked ? 'line-through text-slate-400' : 'text-white'}`}>
                            {p.name}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-extrabold uppercase tracking-wide border ${
                              isStaff
                                ? 'bg-cyan-950 text-cyan-300 border-cyan-800'
                                : 'bg-amber-950 text-amber-300 border-amber-800'
                            }`}
                          >
                            {isStaff ? 'Staff' : 'Visitor'}
                          </span>
                          {p.company && (
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">
                              {p.company}
                            </span>
                          )}
                          {p.role && isStaff && (
                            <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400">
                              {p.role}
                            </span>
                          )}
                          {p.is_off_site && (
                            <span className="text-[11px] font-bold px-2 py-0.5 rounded-lg bg-amber-950 text-amber-300 border border-amber-500/40 flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-amber-400" />
                              Off-Site: {p.site_name || 'Client Site'}
                            </span>
                          )}
                        </div>

                        <div className="text-xs text-slate-400 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                          {p.visiting_host && (
                            <span>
                              Host: <strong className="text-cyan-400">{p.visiting_host}</strong>
                            </span>
                          )}
                          {p.email && <span>Email: {p.email}</span>}
                          {p.registration && <span>Vehicle: {p.registration}</span>}
                        </div>
                      </div>

                      {/* Time */}
                      <div className="text-right text-xs flex-shrink-0">
                        <div className="text-slate-400">Signed In:</div>
                        <div className="font-mono text-emerald-400 font-bold">
                          {p.check_in_time
                            ? new Date(p.check_in_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : 'Active'}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Checklist progress status footer */}
            <div className="px-6 py-3 bg-slate-950/80 border-t border-white/10 flex items-center justify-between text-xs text-slate-400">
              <div>
                Verified Safe: <strong className="text-emerald-400 font-mono">{checkedIds.size}</strong> of{' '}
                <strong className="text-white font-mono">{allPersonnel.length}</strong> personnel
              </div>
              <div className="text-slate-500">Assemble at Designated Assembly Point A</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
